// Identity colour: stable, derived, and never the only signal.
//
// ── DERIVED FROM THE ID, NOT STORED ─────────────────────────────────────────────
// A colour column would need an admin screen, a uniqueness rule and a migration. Hashing the id gives
// a colour that is stable across sessions, devices and reloads for free, and cannot drift out of sync
// with the roster because there is nothing to keep in sync.
//
// ── HUE ALONE IS NEVER THE MESSAGE ──────────────────────────────────────────────
// Roughly one man in twelve has a colour vision deficiency, and this app is used by a field crew on a
// phone in daylight. Every chip therefore carries INITIALS as well as a hue, and every STATUS carries
// a shape or a word as well as a colour. Colour is the fast path, not the only path.

/** FNV-1a. Small, deterministic, and good enough to spread ids across a palette. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Twelve hues at even spacing, offset off the primary axes so nothing lands on the status colours
 * (red for overload, amber for conflict, green for done). Chroma and lightness are fixed so every
 * identity reads at the same weight — a palette where one person is neon is a palette that says that
 * person matters more.
 */
const HUES = [12, 42, 72, 102, 132, 162, 192, 222, 252, 282, 312, 342];

export interface IdentityColour { bg: string; border: string; text: string; hue: number }

export function identityColour(id: string, isTeam = false): IdentityColour {
  const hue = HUES[hash(id) % HUES.length];
  // A TEAM READS DIFFERENTLY FROM A PERSON at a glance: same hue family, lower chroma and a heavier
  // border. Two different KINDS of thing must not be distinguishable only by which hue they landed on.
  const c = isTeam ? 0.06 : 0.11;
  return {
    bg: `oklch(0.72 ${c} ${hue} / 0.22)`,
    border: `oklch(0.72 ${c + 0.06} ${hue})`,
    text: `oklch(0.88 ${c} ${hue})`,
    hue,
  };
}

/**
 * Initials, which are what actually distinguishes two chips when the hues collide — and they will
 * collide, because twelve hues cannot separate twenty people. `display_name` has no uniqueness
 * constraint in the database, so this is a hint and never an identifier.
 */
export function initials(name: string | null): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// `loadLevel` USED TO LIVE HERE, with an 85%-is-full threshold, while the badge rendered beside it in
// the same cell used a different rule. Two answers to "is this person full" inside one calendar is how
// a screen ends up disagreeing with itself — and that disagreement is what Alex saw. It now lives in
// lib/capacity.ts next to the numbers it describes, and there is exactly one of it.
